/**
 * Lift2Fit — admin: Audit (`#/admin/audit`).
 *
 * The append-only trail, newest first: who changed which account, what exactly
 * changed, when, and the before/after snapshot. This is the accountability half
 * of the admin surface — a write without a row here should be impossible.
 */
import { useState } from 'react';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { AlertIcon, ShieldIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { ADMIN_EYEBROW, formatCount, roleLabel } from './shared';
import type { Role } from '@/lib/api';

const ROLE_CODES: ReadonlyArray<Role> = ['lifter', 'trainer', 'coach', 'admin'];

/** Pretty-print one snapshot without ever leaking more than the four fields. */
function snapshotText(snapshot: Record<string, unknown> | null): string {
  if (!snapshot) return '—';
  const parts: string[] = [];
  if (Array.isArray(snapshot.roles)) {
    const labels = snapshot.roles
      .filter((role): role is string => typeof role === 'string')
      .map((role) => (ROLE_CODES.includes(role as Role) ? roleLabel(role as Role) : role));
    parts.push(`roles ${labels.join(', ')}`);
  }
  if (typeof snapshot.coachPlan === 'string') parts.push(`plan ${snapshot.coachPlan}`);
  if (typeof snapshot.tier === 'string') parts.push(`tier ${snapshot.tier}`);
  if ('premiumUntil' in snapshot) {
    parts.push(`premium ${typeof snapshot.premiumUntil === 'string' ? snapshot.premiumUntil.slice(0, 10) : 'none'}`);
  }
  // Catalog/offer/invitation snapshots carry their own keys; show a compact,
  // bounded summary rather than dumping the whole object.
  if (parts.length === 0) {
    for (const [key, value] of Object.entries(snapshot).slice(0, 5)) {
      if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        parts.push(`${key} ${String(value).slice(0, 40)}`);
      }
    }
  }
  return parts.join(' · ') || '—';
}

/** The human label for an audit target: account email, or the catalog key. */
function targetLabel(entry: { targetKind: string; targetEmail: string | null; targetRef: string | null; targetUserId: string | null }): {
  text: string;
  userId: string | null;
} {
  if (entry.targetKind === 'account') {
    return {
      text: entry.targetEmail ?? entry.targetUserId ?? 'deleted account',
      userId: entry.targetUserId,
    };
  }
  return { text: entry.targetRef ?? entry.targetKind, userId: null };
}

export function AdminAuditScreen() {
  const [limit, setLimit] = useState(50);
  const state = useAsync(() => api.adminAudit(limit), [limit]);
  const trail = state.data;

  if (state.loading && !trail) {
    return <LoadingPanel label="Loading the audit trail" rows={5} />;
  }

  if (!trail) {
    return (
      <EmptyState
        title="We could not read the audit trail"
        body={state.error ?? 'The admin API did not answer. Sign in as an administrator and try again.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-audit">
      <ScreenHeader
        eyebrow={`${ADMIN_EYEBROW} · accountability`}
        title="Audit"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Every deliberate admin write, newest first, with its before/after. Append-only: the trail is the record of what the platform owner did."
      />

      <Panel
        title={`${formatCount(trail.total)} entr${trail.total === 1 ? 'y' : 'ies'}`}
        subtitle="Actor, account, summary and the two snapshots."
        testId="admin-audit-list"
      >
        {trail.data.length === 0 ? (
          <EmptyState
            title="No admin writes yet"
            body="When an account's plan, tier or roles are changed from the admin surface, the change lands here with its before and after."
            icon={<ShieldIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <ul className="flex flex-col divide-y divide-hairline">
            {trail.data.map((entry) => (
              <li
                key={entry.auditId}
                data-testid={`admin-audit-row-${entry.auditId}`}
                className="flex flex-col gap-1 py-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[13px] font-bold text-ink">{entry.summary}</span>
                  <span className="text-[12px] text-ink-muted">{formatDateTime(entry.createdAt)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-muted">
                  <span>
                    by <span className="text-ink">{entry.actorEmail ?? 'unknown admin'}</span>
                  </span>
                  <span>
                    target{' '}
                    {(() => {
                      const target = targetLabel(entry);
                      return target.userId ? (
                        <Link to={`/admin/accounts/${target.userId}`} className="text-accent hover:text-accent-hover">
                          {target.text}
                        </Link>
                      ) : (
                        target.text
                      );
                    })()}
                  </span>
                  <span className="font-mono">{entry.targetKind}</span>
                </div>
                <div className="flex flex-col gap-0.5 text-[11px] text-ink-muted sm:flex-row sm:gap-6">
                  <span>before: {snapshotText(entry.before)}</span>
                  <span>after: {snapshotText(entry.after)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {trail.hasMore ? (
          <button
            type="button"
            data-testid="admin-audit-more"
            onClick={() => setLimit((current) => current + 50)}
            className="inline-flex h-9 w-fit items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Load more
          </button>
        ) : null}
      </Panel>
    </div>
  );
}
